<template>
  <div class="breadcrumb-container-wrapper" :class="{ showJumpTo }">
    <div
      class="breadcrumb-container"
      :class="{ 'flex-container-row': showJumpTo }"
    >
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="{ path: rootPath }">
              <span class="icon-home"></span>
            </router-link>
          </li>
          <slot name="breadcrumbs">
            <span class="breadcrumb-separator"> > </span>

            <li
              v-for="(breadcrumb, index) in breadcrumbs"
              :key="index"
              class="breadcrumb-item"
            >
              <span v-if="index > 0" class="breadcrumb-separator"> > </span>

              <router-link
                v-if="!breadcrumb.isActive"
                :to="{ name: breadcrumb.to.name }"
              >
                {{ breadcrumb.label }}
              </router-link>

              <span v-else>{{ breadcrumb.label }}</span>
            </li>

            <li v-if="parameterBreadcrumb" class="breadcrumb-item">
              <span class="breadcrumb-separator"> > </span>
              <span class="current">{{ parameterBreadcrumb }}</span>
            </li>
          </slot>
        </ol>
      </nav>
      <slot name="loanservice-banner"></slot>
    </div>
    <slot name="jumpto"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

interface Breadcrumb {
  label: string;
  to: { name: string; path: string };
  rootPath?: string;
  isActive: boolean;
}

const props = withDefaults(
  defineProps<{ rootPath: string; showJumpTo?: boolean }>(),
  {
    showJumpTo: false,
  }
);

const route = useRoute();

const breadcrumbs = computed<Breadcrumb[]>(() => {
  let accumulatedPath = props.rootPath;

  return route.matched.map((match, index): Breadcrumb => {
    const label =
      (match.meta?.breadcrumb as string) || match.name?.toString() || "???";

    if (match.meta?.breadcrumbDetails) {
      const breadcrumbDetails = match.meta?.breadcrumbDetails as Breadcrumb;

      return {
        label,
        to: {
          name: breadcrumbDetails?.to?.name || label,
          path: breadcrumbDetails.to?.path,
        },
        isActive: breadcrumbDetails.isActive,
      };
    }

    if (index > 0) {
      accumulatedPath += `/${match.path.split("/").pop()}`;
    }

    return {
      label,
      to: { name: label, path: accumulatedPath },
      isActive: index === route.matched.length,
    };
  });
});

const parameterBreadcrumb = computed(() => {
  const paramValue =
    route.params.id || route.params.referenceId || route.params.batchId;
  return paramValue &&
    paramValue !== breadcrumbs.value[breadcrumbs.value.length - 1]?.label
    ? paramValue
    : null;
});
</script>

<style scoped>
.breadcrumb-container {
  background-color: #d4d6dd;
  width: 100%;
  padding: 1rem 2.5rem;
}

@media (max-width: 960px) {
  .breadcrumb-container {
    padding: 1rem;
  }
}

.breadcrumb {
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.breadcrumb-item span {
  font-weight: bold;
  color: black;
}

.breadcrumb-item > .current {
  color: #555;
  font-weight: bold;
}

.breadcrumb-separator {
  margin: 0 0.5rem;
}

.breadcrumb-item > a {
  text-decoration: none;
  color: #000;
}

.breadcrumb-item > a:hover .icon-home {
  text-decoration: none;
}

.breadcrumb-container.flex-container-row {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.breadcrumb-container-wrapper.showJumpTo {
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #d4d6dd;
  padding-right: 13px;
}

.breadcrumb-container-wrapper.showJumpTo .breadcrumb-container {
  padding-left: 15px;
  padding-right: 0;
  max-width: calc(100% - var(--jumpToContainerWidth));
}

@media screen and (max-width: 992px) {
  .breadcrumb-container-wrapper.showJumpTo {
    padding-right: 6px;
  }
}
</style>
